import { ApolloLink, FetchResult, Observable } from "@apollo/client";
import { throwServerError } from "@apollo/client/link/utils";
import { Auth } from "aws-amplify";
import { Credentials } from "@aws-amplify/core";
import { Signer } from "aws-appsync-auth-link/lib/signer";
import { print } from "graphql/language/printer";

//Replaces the createAuthLink(AWS_IAM) + HttpLink pair for queries/mutations.
//aws-appsync-auth-link signs a body it builds itself while HttpLink re-serializes and sends its own body,
//so anything that makes the two differ produces "The request signature we calculated does not match...".
//This link serializes the body once, signs those exact bytes and sends them itself, so the signed and
//sent payloads can never drift apart. It also retries with freshly fetched credentials when AppSync
//rejects the signature, which covers bad/partial credentials handed back by Amplify around the
//1 hour guest credential refresh.

const MAX_ATTEMPTS = 3;

interface ISigV4LinkOptions {
    url: string;
    region: string;
}

const isRetryableAuthFailure = (statusCode: number, resultText: string) => {
    if (statusCode === 401 || statusCode === 403) return true;
    return statusCode === 400 && resultText.includes("signature we calculated does not match");
};

const getFreshCredentials = async () => {
    const rawCredentials: any = await Auth.currentCredentials();
    return {
        credentials: Auth.essentialCredentials(rawCredentials),
        expiration: rawCredentials && (rawCredentials.expiration || rawCredentials.expireTime) ? new Date(rawCredentials.expiration || rawCredentials.expireTime).toISOString() : null,
    };
};

export const createIamSigV4Link = ({ url, region }: ISigV4LinkOptions) =>
    new ApolloLink(
        (operation) =>
            new Observable<FetchResult>((observer) => {
                const controller = new AbortController();
                let closed = false;

                const execute = async () => {
                    const { host, pathname } = new URL(url);
                    const body = JSON.stringify({
                        operationName: operation.operationName,
                        variables: operation.variables,
                        query: print(operation.query),
                    });

                    for (let attempt = 1; attempt <= MAX_ATTEMPTS; attempt++) {
                        //On the last attempt drop Amplify's in-memory credentials so they are re-derived from Cognito
                        //instead of reusing a potentially broken cached set. Does not touch the stored identity id.
                        if (attempt === MAX_ATTEMPTS) {
                            try {
                                await Credentials.clear();
                            } catch (e) {}
                        }

                        const { credentials, expiration } = await getFreshCredentials();

                        const request = {
                            method: "POST",
                            url: url,
                            host: host,
                            path: pathname,
                            service: "appsync",
                            region: region,
                            headers: {
                                accept: "*/*",
                                "content-type": "application/json; charset=UTF-8",
                            },
                            body: body,
                        };

                        const signed = Signer.sign(request, {
                            access_key: credentials.accessKeyId,
                            secret_key: credentials.secretAccessKey,
                            session_token: credentials.sessionToken,
                        });

                        //The browser sets the Host header itself and some fetch polyfills throw if we try to.
                        const { host: hostHeader, ...headers } = signed.headers;

                        const response = await fetch(url, {
                            method: "POST",
                            headers: headers,
                            body: body,
                            signal: controller.signal,
                        });

                        const resultText = await response.text();

                        if (!response.ok && isRetryableAuthFailure(response.status, resultText) && attempt < MAX_ATTEMPTS) {
                            continue;
                        }

                        let result;
                        try {
                            result = JSON.parse(resultText);
                        } catch (e) {
                            return throwServerError(response, resultText, `Response not successful: Received status code ${response.status}`);
                        }

                        if (!response.ok) {
                            const serverError: any = new Error(`Response not successful: Received status code ${response.status}`);
                            serverError.name = "ServerError";
                            serverError.response = response;
                            serverError.statusCode = response.status;
                            serverError.result = result;
                            serverError.signingDiagnostics = {
                                attempts: attempt,
                                accessKeyId: credentials.accessKeyId,
                                credentialsExpiration: expiration,
                                authenticated: credentials.authenticated,
                                userAgent: typeof navigator !== "undefined" ? navigator.userAgent : null,
                                timeSincePageLoadMs: typeof performance !== "undefined" ? Math.round(performance.now()) : null,
                            };
                            throw serverError;
                        }

                        return result;
                    }
                };

                execute()
                    .then((result) => {
                        if (closed) return;
                        observer.next(result as FetchResult);
                        observer.complete();
                    })
                    .catch((error) => {
                        if (closed) return;
                        observer.error(error);
                    });

                return () => {
                    closed = true;
                    controller.abort();
                };
            }),
    );
